import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Space,
  Tag,
  Popconfirm,
  Typography,
  Select,
  Card,
  Alert,
  Row,
  Col,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  DeleteOutlined,
  EditOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import { commandTemplateAPI } from '../services/api';

const { TextArea } = Input;
const { Title, Text } = Typography;

interface CommandTemplate {
  id: number;
  template_key: string;
  template_name: string;
  template_text: string;
  category: string;
  variables: string | null;
  parse_mode: string;
  description: string | null;
  status: number;
  create_time: string;
  update_time: string;
}

const CommandTemplates: React.FC = () => {
  const [templates, setTemplates] = useState<CommandTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<CommandTemplate | null>(null);
  const [filters, setFilters] = useState<{
    category?: string;
    status?: number;
  }>({});
  const [form] = Form.useForm();

  // 获取模板列表
  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await commandTemplateAPI.getAll(filters);
      setTemplates(response.data.data);
    } catch (error: any) {
      message.error('获取模板列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, [filters]);

  // 打开新建/编辑弹窗
  const openModal = (template?: CommandTemplate) => {
    if (template) {
      setEditingTemplate(template);
      form.setFieldsValue(template);
    } else {
      setEditingTemplate(null);
      form.resetFields();
      form.setFieldsValue({
        category: 'response',
        parse_mode: 'HTML',
        status: 1,
      });
    }
    setModalVisible(true);
  };

  // 提交表单
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (editingTemplate) {
        // 更新
        await commandTemplateAPI.update(editingTemplate.id, values);
        message.success('模板更新成功');
      } else {
        // 创建
        await commandTemplateAPI.create(values);
        message.success('模板创建成功');
      }

      setModalVisible(false);
      form.resetFields();
      fetchTemplates();
    } catch (error: any) {
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error(editingTemplate ? '更新模板失败' : '创建模板失败');
      }
    }
  };

  // 删除模板
  const handleDelete = async (id: number) => {
    try {
      await commandTemplateAPI.delete(id);
      message.success('模板删除成功');
      fetchTemplates();
    } catch (error: any) {
      message.error('删除模板失败');
    }
  };

  // 清除缓存
  const handleClearCache = async () => {
    try {
      await commandTemplateAPI.clearCache();
      message.success('缓存已清除，模板更新将立即生效');
    } catch (error: any) {
      message.error('清除缓存失败');
    }
  };

  // 表格列定义
  const columns = [
    {
      title: '模板键',
      dataIndex: 'template_key',
      width: 200,
      render: (text: string) => <Text code>{text}</Text>,
    },
    {
      title: '模板名称',
      dataIndex: 'template_name',
      width: 150,
    },
    {
      title: '模板文本',
      dataIndex: 'template_text',
      ellipsis: true,
      render: (text: string) => (
        <Text ellipsis={{ tooltip: text }} style={{ maxWidth: 300 }}>
          {text}
        </Text>
      ),
    },
    {
      title: '分类',
      dataIndex: 'category',
      width: 100,
      render: (category: string) => {
        const colorMap: any = {
          response: 'blue',
          success: 'green',
          error: 'red',
        };
        const nameMap: any = {
          response: '响应',
          success: '成功',
          error: '错误',
        };
        return <Tag color={colorMap[category]}>{nameMap[category] || category}</Tag>;
      },
    },
    {
      title: '变量',
      dataIndex: 'variables',
      width: 150,
      render: (variables: string | null) => {
        if (!variables) return '-';
        try {
          const vars = JSON.parse(variables);
          const varNames = Object.keys(vars);
          if (varNames.length === 0) return '-';
          return (
            <Text code style={{ fontSize: 11 }}>
              {varNames.map((v) => `{${v}}`).join(', ')}
            </Text>
          );
        } catch {
          return '-';
        }
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 80,
      render: (status: number) =>
        status === 1 ? (
          <Tag icon={<CheckCircleOutlined />} color="success">
            启用
          </Tag>
        ) : (
          <Tag icon={<CloseCircleOutlined />} color="default">
            禁用
          </Tag>
        ),
    },
    {
      title: '操作',
      width: 150,
      fixed: 'right' as const,
      render: (_: any, record: CommandTemplate) => (
        <Space size="small">
          <Button size="small" icon={<EditOutlined />} onClick={() => openModal(record)}>
            编辑
          </Button>
          <Popconfirm
            title="确定删除此模板吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Title level={3}>命令响应模板管理</Title>

      <Alert
        message="响应模板说明"
        description={
          <>
            <p>
              • 这里可以修改所有命令的响应文本格式，修改后机器人的回复内容会相应改变
            </p>
            <p>• 业务逻辑保持不变，只修改文本内容</p>
            <p>
              • 支持变量替换，如 <Text code>{'{rate}'}</Text>{' '}
              会被替换为实际的汇率值
            </p>
            <p>• 支持 HTML 格式（粗体、斜体、代码等）</p>
            <p>
              • 修改后有1分钟缓存，可以点击"清除缓存"按钮立即生效
            </p>
          </>
        }
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      {/* 筛选和操作栏 */}
      <div style={{ marginBottom: 16 }}>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Select
              placeholder="按分类筛选"
              allowClear
              style={{ width: '100%' }}
              onChange={(value) => setFilters({ ...filters, category: value })}
            >
              <Select.Option value="response">正常响应</Select.Option>
              <Select.Option value="success">成功提示</Select.Option>
              <Select.Option value="error">错误提示</Select.Option>
            </Select>
          </Col>
          <Col span={6}>
            <Select
              placeholder="按状态筛选"
              allowClear
              style={{ width: '100%' }}
              onChange={(value) => setFilters({ ...filters, status: value })}
            >
              <Select.Option value={1}>启用</Select.Option>
              <Select.Option value={0}>禁用</Select.Option>
            </Select>
          </Col>
        </Row>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchTemplates}>
            刷新
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>
            新建模板
          </Button>
          <Button icon={<ClearOutlined />} onClick={handleClearCache}>
            清除缓存
          </Button>
        </Space>
      </div>

      {/* 表格 */}
      <Table
        columns={columns}
        dataSource={templates}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 20 }}
        scroll={{ x: 1200 }}
      />

      {/* 新建/编辑弹窗 */}
      <Modal
        title={editingTemplate ? '编辑响应模板' : '新建响应模板'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={700}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            label="模板键"
            name="template_key"
            rules={[{ required: true, message: '请输入模板键' }]}
            extra="唯一标识，用于代码中引用（如：query_rate）"
          >
            <Input
              placeholder="query_rate"
              disabled={!!editingTemplate}
              maxLength={100}
            />
          </Form.Item>

          <Form.Item
            label="模板名称"
            name="template_name"
            rules={[{ required: true, message: '请输入模板名称' }]}
          >
            <Input placeholder="查询汇率" maxLength={200} />
          </Form.Item>

          <Form.Item
            label="模板文本"
            name="template_text"
            rules={[{ required: true, message: '请输入模板文本' }]}
            extra={
              <>
                支持变量替换，使用 {'{'}变量名{'}'} 格式，如：
                <Text code>当前汇率为 {'{rate}'}</Text>
              </>
            }
          >
            <TextArea
              placeholder="当前汇率为 {rate}"
              rows={4}
              showCount
              maxLength={1000}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="分类"
                name="category"
                rules={[{ required: true }]}
              >
                <Select>
                  <Select.Option value="response">正常响应</Select.Option>
                  <Select.Option value="success">成功提示</Select.Option>
                  <Select.Option value="error">错误提示</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="解析模式" name="parse_mode">
                <Select>
                  <Select.Option value="HTML">HTML</Select.Option>
                  <Select.Option value="Markdown">Markdown</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="可用变量（JSON格式）"
            name="variables"
            extra={
              <>
                定义可用的变量，格式：
                <Text code>{'{\"rate\":\"汇率值\",\"amount\":\"金额\"}'}</Text>
              </>
            }
          >
            <TextArea
              placeholder='{"rate":"汇率值","amount":"金额"}'
              rows={2}
            />
          </Form.Item>

          <Form.Item label="说明" name="description">
            <TextArea placeholder="模板说明" rows={2} />
          </Form.Item>

          <Form.Item label="状态" name="status">
            <Select>
              <Select.Option value={1}>启用</Select.Option>
              <Select.Option value={0}>禁用</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingTemplate ? '更新' : '创建'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CommandTemplates;
